import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { useEffect } from 'react'
import { LuCheck } from 'react-icons/lu'
import { cn } from '@cannect/lib/utils'
import { Skeleton } from '@cannect/new-components/atoms/Skeleton'
import { formatToBRL } from '@cannect/utils/formatters'
import { CycleSelector } from '../CycleSelector'
import { recurrencyCopy } from '../copy'
import type { ProductRecurrency, RecurrencyChoice } from './useProductRecurrency'

export * from './useProductRecurrency'

type RecurrencyPurchaseSelectorProps = {
  recurrency: ProductRecurrency
  value: RecurrencyChoice
  onChange: (choice: RecurrencyChoice) => void
  // Preço de compra única já exibido na PDP (usado só se o serviço não mandar `unitPrice`).
  fallbackUnitPrice?: number
  volumeLabel?: string | null
}

const optionClass =
  'flex w-full items-center gap-3 rounded-xl border border-solid p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700'

const Radio = ({ id, value, disabled, label }: { id: string; value: string; disabled?: boolean; label: string }) => (
  <RadioGroupPrimitive.Item
    id={id}
    value={value}
    disabled={disabled}
    aria-label={label}
    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-solid border-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 disabled:cursor-not-allowed data-[state=checked]:border-primary-700">
    <RadioGroupPrimitive.Indicator className="h-2.5 w-2.5 rounded-full bg-primary-700" />
  </RadioGroupPrimitive.Item>
)

// "Como você quer comprar?" (mockup da PDP). Valores sempre do serviço (B2);
// sem eles, só o percentual. Nada aparece para produto `hidden`.
export const RecurrencyPurchaseSelector = ({
  recurrency,
  value,
  onChange,
  fallbackUnitPrice,
  volumeLabel
}: RecurrencyPurchaseSelectorProps) => {
  const { state, item, summary, isLoading, documentsPending } = recurrency
  const unavailable = state === 'unavailable'
  const percent = summary?.subscriberDiscountPercent
  const suggested = item?.suggestedCycleDays || summary?.suggestedCycleDays || null
  const options = summary?.cycleDaysOptions?.length ? summary.cycleDaysOptions : [60, 90, 120]

  useEffect(() => {
    if (state !== 'available' || value.cycleDays || !suggested) return
    onChange({ ...value, cycleDays: suggested })
  }, [state, suggested, value.cycleDays])

  useEffect(() => {
    if (unavailable && value.mode === 'subscription') onChange({ ...value, mode: 'once' })
  }, [unavailable, value.mode])

  if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />
  if (state === 'hidden') return null

  const onceUnitPrice = item?.unitPrice ?? fallbackUnitPrice
  let subscriptionDescription: string | null = null
  if (unavailable) subscriptionDescription = recurrencyCopy.purchase.unavailable
  else if (percent) subscriptionDescription = recurrencyCopy.purchase.subscriptionDescription(percent)
  const isSubscription = value.mode === 'subscription' && !unavailable
  const cycleLabels: Partial<Record<number, string>> = {
    60: recurrencyCopy.purchase.cycleHintIntense,
    90: recurrencyCopy.purchase.cycleHintPopular
  }
  if (suggested) cycleLabels[suggested] = recurrencyCopy.purchase.cycleHintSuggested(volumeLabel)

  return (
    <section aria-labelledby="recurrency-purchase-title" className="flex w-full flex-col gap-3">
      <h2 id="recurrency-purchase-title" className="text-base font-semibold text-gray-900">
        {recurrencyCopy.purchase.title}
      </h2>
      <RadioGroupPrimitive.Root
        value={isSubscription ? 'subscription' : 'once'}
        onValueChange={(mode) => onChange({ ...value, mode: mode as RecurrencyChoice['mode'] })}
        className="flex flex-col gap-3">
        <label htmlFor="recurrency-mode-once" className={cn(optionClass, 'cursor-pointer border-gray-300 bg-white')}>
          <Radio id="recurrency-mode-once" value="once" label={recurrencyCopy.purchase.onceTitle} />
          <span className="flex flex-1 flex-col">
            <span className="text-sm font-semibold text-gray-900">{recurrencyCopy.purchase.onceTitle}</span>
            <span className="text-xs text-gray-600">{recurrencyCopy.purchase.onceDescription}</span>
          </span>
          {onceUnitPrice !== undefined && (
            <span className="text-base font-semibold text-gray-900">{formatToBRL(onceUnitPrice)}</span>
          )}
        </label>

        <div
          className={cn(
            'flex flex-col rounded-xl border border-solid',
            isSubscription ? 'border-2 border-primary-700 bg-gray-50' : 'border-gray-300 bg-white',
            unavailable && 'opacity-60'
          )}>
          <label
            htmlFor="recurrency-mode-subscription"
            className={cn(optionClass, 'border-0', unavailable ? 'cursor-not-allowed' : 'cursor-pointer')}>
            <Radio
              id="recurrency-mode-subscription"
              value="subscription"
              disabled={unavailable}
              label={recurrencyCopy.purchase.subscriptionTitle}
            />
            <span className="flex flex-1 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
                {recurrencyCopy.purchase.subscriptionTitle}
                {percent ? (
                  <span className="rounded-full bg-primary-700 px-2 py-0.5 text-xs font-semibold text-white">
                    {recurrencyCopy.purchase.discountChip(percent)}
                  </span>
                ) : null}
              </span>
              <span className="text-xs text-gray-600">{subscriptionDescription}</span>
            </span>
            {!unavailable && item?.subscriberUnitPrice !== undefined && (
              <span className="flex flex-col items-end">
                {item.unitPrice !== undefined && (
                  <span className="text-xs text-gray-500 line-through">{formatToBRL(item.unitPrice)}</span>
                )}
                <span className="text-lg font-semibold text-gray-900">{formatToBRL(item.subscriberUnitPrice)}</span>
              </span>
            )}
          </label>

          {isSubscription && (
            <div className="flex flex-col gap-3 border-0 border-t border-solid border-gray-200 p-4">
              <CycleSelector
                options={options}
                value={value.cycleDays}
                suggested={suggested}
                labels={cycleLabels}
                onChange={(cycleDays) => onChange({ ...value, cycleDays })}
              />
              <ul className="flex list-none flex-col gap-2 p-0">
                {recurrencyCopy.purchase.bullets().map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2 text-sm text-gray-700">
                    <LuCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
                    {bullet}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-600">
                {recurrencyCopy.purchase.footer(summary?.deliveryEstimateDays, { documentsPending })}
              </p>
            </div>
          )}
        </div>
      </RadioGroupPrimitive.Root>
    </section>
  )
}
